import Supercluster from 'supercluster'
import type { BoxTruckMapDriver } from '../services/boxTruckLoadsApi'
import type { DriverClusterInspection, DriverMapPoint, MapBounds } from '../types/boxTruckMap'
import { BOX_TRUCK_MAP_CLUSTER_INSPECTION_LIMIT, BOX_TRUCK_MAP_CLUSTER_RADIUS, BOX_TRUCK_MAP_MAX_ZOOM } from '../constants/boxTruckMap'

interface DriverFeatureProperties {
    driverId: string
}

type DriverClusterProperties = Record<string, never>

type DriverClusterIndex = Supercluster<DriverFeatureProperties, DriverClusterProperties>

const toDriverFeature = (driver: BoxTruckMapDriver): Supercluster.PointFeature<DriverFeatureProperties> => ({
    type: 'Feature',
    properties: { driverId: driver.driverId },
    geometry: { type: 'Point', coordinates: [driver.lng, driver.lat] },
})

const isClusterFeature = (
    feature: Supercluster.ClusterFeature<DriverClusterProperties> | Supercluster.PointFeature<DriverFeatureProperties>
): feature is Supercluster.ClusterFeature<DriverClusterProperties> => 'cluster' in feature.properties && feature.properties.cluster === true

export const buildDriverClusterIndex = (drivers: BoxTruckMapDriver[]): DriverClusterIndex => {
    const index = new Supercluster<DriverFeatureProperties, DriverClusterProperties>({
        radius: BOX_TRUCK_MAP_CLUSTER_RADIUS,
        maxZoom: BOX_TRUCK_MAP_MAX_ZOOM,
        minPoints: 2,
    })

    return index.load(drivers.map(toDriverFeature))
}

export const getDriverMapPoints = (
    index: DriverClusterIndex,
    driverById: ReadonlyMap<string, BoxTruckMapDriver>,
    bounds: MapBounds,
    zoom: number
): DriverMapPoint[] => {
    const features = index.getClusters([bounds.west, bounds.south, bounds.east, bounds.north], Math.floor(zoom))
    const points: DriverMapPoint[] = []

    for (const feature of features) {
        const [lng, lat] = feature.geometry.coordinates
        if (isClusterFeature(feature)) {
            points.push({
                kind: 'cluster',
                clusterId: feature.properties.cluster_id,
                count: feature.properties.point_count,
                countLabel: feature.properties.point_count_abbreviated,
                position: { lat, lng },
            })
            continue
        }

        const driver = driverById.get(feature.properties.driverId)
        if (driver) points.push({ kind: 'driver', driver })
    }

    return points
}

export const getDriverClusterExpansionZoom = (index: DriverClusterIndex, clusterId: number): number => index.getClusterExpansionZoom(clusterId)

export const getDriverClusterInspection = (
    index: DriverClusterIndex,
    driverById: ReadonlyMap<string, BoxTruckMapDriver>,
    clusterId: number,
    totalCount: number
): DriverClusterInspection => {
    const drivers = index
        .getLeaves(clusterId, BOX_TRUCK_MAP_CLUSTER_INSPECTION_LIMIT)
        .map((feature) => driverById.get(feature.properties.driverId))
        .filter((driver): driver is BoxTruckMapDriver => Boolean(driver))
        .sort((first, second) => (first.name ?? '').localeCompare(second.name ?? ''))

    return { drivers, totalCount }
}
